import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  BookOpen,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Send,
  Play,
  Lock,
  ArrowRight,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import { useLearnerAuth } from "@/contexts/LearnerAuthContext";
import { sanitizeHtml } from "@/utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearnerTrackDetail() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { learner } = useLearnerAuth();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Inline assessment state
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [assessmentItems, setAssessmentItems] = useState([]);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [responses, setResponses] = useState({});
  const [feedback, setFeedback] = useState({});
  const [submittingItem, setSubmittingItem] = useState(null);
  const [completingAssessment, setCompletingAssessment] = useState(false);

  const fetchTrack = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/learner/tracks/${trackId}`, { withCredentials: true });
      setTrack(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load track";
      toast.error(msg);
      navigate("/portal/dashboard");
    }
    finally { setLoading(false); }
  }, [trackId, navigate]);

  useEffect(() => { fetchTrack(); }, [fetchTrack]);

  const openSubmitDialog = async () => {
    try {
      const { data } = await axios.get(`${API}/learner/tracks/${trackId}/submission-info`, { withCredentials: true });
      setSubInfo(data);
      if (data.already_submitted) {
        toast.info("You've already submitted this track.");
      } else {
        setShowSubmit(true);
      }
    } catch { toast.error("Failed to load submission info"); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/learner/tracks/${trackId}/submit`, {}, { withCredentials: true });
      toast.success("Track submitted!");
      setShowSubmit(false);
      fetchTrack();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  // ── Inline Assessment Logic ──
  const expandAssessment = async (atId) => {
    if (expandedAssessment === atId) { setExpandedAssessment(null); return; }
    setExpandedAssessment(atId);
    setAssessmentLoading(true);
    setAssessmentItems([]);
    setResponses({});
    setFeedback({});
    try {
      const { data } = await axios.get(`${API}/learner/tracks/${trackId}/assessments/${atId}/items`, { withCredentials: true });
      setAssessmentItems(data);
      const existing = {};
      data.forEach((item) => { if (item.response) existing[item.id] = item.response; });
      setFeedback(existing);
    } catch { toast.error("Failed to load assessment items"); }
    finally { setAssessmentLoading(false); }
  };

  const setItemResponse = (itemId, value) => {
    setResponses((p) => ({ ...p, [itemId]: value }));
  };

  const submitItemResponse = async (item) => {
    const resp = responses[item.id];
    if (!resp) return;
    setSubmittingItem(item.id);
    try {
      const body = {};
      if (item.item_type === "mc") {
        body.selected_option_ids = [resp];
      } else if (item.item_type === "sata") {
        body.selected_option_ids = resp;
      } else if (item.item_type === "short_answer" || item.item_type === "essay") {
        body.text_response = resp;
      } else if (item.item_type === "likert") {
        body.likert_value = resp;
      }
      const { data } = await axios.post(
        `${API}/learner/tracks/${trackId}/assessments/${expandedAssessment}/items/${item.id}/respond`,
        body, { withCredentials: true }
      );
      setFeedback((p) => ({ ...p, [item.id]: data }));
      toast.success("Response recorded");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setSubmittingItem(null); }
  };

  const completeAssessment = async (atId) => {
    setCompletingAssessment(true);
    try {
      const { data } = await axios.post(`${API}/learner/tracks/${trackId}/assessments/${atId}/complete`, {}, { withCredentials: true });
      toast.success(`Assessment completed! Score: ${data.score}%`);
      setExpandedAssessment(null);
      fetchTrack();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to complete assessment"); }
    finally { setCompletingAssessment(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} /></div>;
  if (!track) return null;

  const items = track.items || [];
  const completedCount = items.filter((i) => i.completed).length;
  const progress = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  // Sequential progression: the first non-completed item is the "current"
  // one. Items before it are `completed`, items after it are `locked` and
  // are visually dimmed + not navigable. Learners can only interact with
  // the current item at a time.
  const currentIdx = items.findIndex((i) => !i.completed);
  const allComplete = items.length > 0 && currentIdx === -1;
  // The header "Continue →" button always navigates to the current
  // incomplete LEXP if one exists. If the current incomplete item is an
  // assessment, we fall back to the first incomplete LEXP anywhere in
  // the track.
  const currentLexp =
    (currentIdx !== -1 && items[currentIdx].type === "lexp" ? items[currentIdx] : null) ||
    items.find((i) => i.type === "lexp" && !i.completed) ||
    null;
  const continueHref = currentLexp
    ? `/portal/learning/${currentLexp.lexp_id}?trackId=${trackId}`
    : null;

  const canSubmit = allComplete && !track.submitted;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="learner-track-detail">
      <Link to="/portal/learning" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-body" data-testid="track-back-link">
        <ArrowLeft className="h-4 w-4" />Back to Learning
      </Link>

      {/* Header — title on the left, Submit Track + Continue on the right */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-bold text-gray-900 tracking-tight" data-testid="track-title">{track.title}</h1>
          {track.description && <p className="text-gray-500 font-body mt-1">{track.description}</p>}
        </div>
        {!track.submitted && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={openSubmitDialog}
              disabled={!canSubmit}
              variant="outline"
              className={`h-10 px-4 text-sm font-body rounded-lg border-gray-200 ${
                canSubmit
                  ? "bg-white text-gray-800 hover:bg-gray-50"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
              data-testid="track-submit-btn"
            >
              Submit Track
            </Button>
            {continueHref && (
              <Link to={continueHref} data-testid="track-header-continue-link">
                <Button
                  className="h-10 px-4 text-sm font-body font-semibold rounded-lg text-white hover:opacity-90 inline-flex items-center gap-1"
                  style={{ backgroundColor: "#10b981" }}
                  data-testid="track-header-continue-btn"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="track-progress">
        <div className="flex justify-between items-center">
          <span className="text-sm font-body font-medium text-gray-700">{completedCount} of {items.length} items completed</span>
          <span className="text-sm font-body font-bold" style={{ color: "var(--primary-color, #4A90D9)" }}>{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: "var(--primary-color, #4A90D9)" }} />
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-3" data-testid="track-items-list">
        {items.map((item, idx) => {
          const isCurrent = idx === currentIdx;
          const isFuture = currentIdx !== -1 && idx > currentIdx;
          const isDone = item.completed;
          // Row styling based on sequential position.
          let rowClass = "bg-white border-gray-200";
          if (isCurrent) rowClass = "bg-emerald-50 border-emerald-300";
          const rowOpacityStyle = isFuture ? { opacity: 0.35, pointerEvents: "none" } : undefined;
          // Status icon: green check for done, green play for current,
          // lock for future.
          let StatusIcon = Circle;
          let statusIconClass = "text-gray-300";
          if (isDone) {
            StatusIcon = CheckCircle2;
            statusIconClass = "text-emerald-500";
          } else if (isCurrent) {
            StatusIcon = Play;
            statusIconClass = "text-emerald-500";
          } else if (isFuture) {
            StatusIcon = Lock;
            statusIconClass = "text-gray-400";
          }
          const nameClass = isCurrent
            ? "text-sm font-body font-bold text-emerald-800"
            : "text-sm font-body font-medium text-gray-900";

          return (
            <div
              key={item.item_id}
              className={`border rounded-xl overflow-hidden transition-all ${rowClass}`}
              style={rowOpacityStyle}
              data-testid={`track-item-${item.item_id}`}
              data-item-state={isDone ? "complete" : isCurrent ? "current" : "future"}
            >
              <div className="p-4 flex items-center gap-3">
                {/* 1. Status icon */}
                <div className="shrink-0">
                  <StatusIcon className={`h-6 w-6 ${statusIconClass}`} />
                </div>
                {/* 2. Step number */}
                <span className="text-xs text-gray-400 font-body shrink-0 tabular-nums">{idx + 1}</span>
                {/* Item-type icon (kept for visual continuity) */}
                <div className="shrink-0">
                  {item.type === "lexp" ? (
                    <BookOpen className={`h-5 w-5 ${isCurrent ? "text-emerald-600" : "text-gray-400"}`} />
                  ) : (
                    <ClipboardList className={`h-5 w-5 ${isCurrent ? "text-emerald-600" : "text-gray-400"}`} />
                  )}
                </div>
                {/* 3. LEXP / assessment name (middle, truncates) */}
                <div className="flex-1 min-w-0">
                  <h3 className={`${nameClass} truncate`} title={item.title}>{item.title}</h3>
                  <p className="text-xs text-gray-500 font-body">{item.type === "lexp" ? "Learning Experience" : "Assessment"}</p>
                </div>
                {/* 4. Right-aligned action indicator */}
                <div className="flex items-center gap-2 shrink-0">
                  {item.type === "lexp" && item.completed && item.is_preliminary && !item.is_finalized && (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs" data-testid={`lexp-score-pending-${item.lexp_id}`}>
                      Score pending
                    </Badge>
                  )}
                  {item.completed && item.score != null && !item.all_survey && !item.is_preliminary && (
                    <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs">{item.score}%</Badge>
                  )}

                  {/* LEXP action indicator */}
                  {item.type === "lexp" && isDone && (
                    <span
                      className="inline-flex items-center justify-center text-xs font-body font-semibold rounded-md border border-teal-300 text-teal-600 bg-teal-50 h-8 px-3"
                      style={{ minWidth: 80 }}
                      data-testid={`lexp-complete-badge-${item.lexp_id}`}
                    >
                      Complete
                    </span>
                  )}
                  {item.type === "lexp" && isCurrent && (
                    <Link
                      to={`/portal/learning/${item.lexp_id}?trackId=${trackId}`}
                      data-testid={`lexp-continue-link-${item.lexp_id}`}
                    >
                      <Button
                        className="h-8 text-xs font-body font-semibold rounded-md text-white hover:opacity-90 inline-flex items-center justify-center gap-1"
                        style={{ backgroundColor: "#10b981", minWidth: 80 }}
                        data-testid={`lexp-continue-btn-${item.lexp_id}`}
                      >
                        Continue <ArrowRight className="h-3 w-3" />
                      </Button>
                    </Link>
                  )}

                  {/* Assessment action indicator — assessments follow the
                      same sequential lock: only the current one can be
                      expanded; future ones are dimmed with a lock icon
                      (row-level opacity handles the "future" state) */}
                  {item.type === "assessment" && isDone && (
                    <span
                      className="inline-flex items-center justify-center text-xs font-body font-semibold rounded-md border border-teal-300 text-teal-600 bg-teal-50 h-8 px-3"
                      style={{ minWidth: 80 }}
                      data-testid={`assessment-complete-badge-${item.assessment_type_id}`}
                    >
                      Complete
                    </span>
                  )}
                  {item.type === "assessment" && isCurrent && (
                    <Button size="sm" className="h-8 text-xs font-body font-semibold rounded-md text-white hover:opacity-90 inline-flex items-center justify-center gap-1"
                      onClick={() => expandAssessment(item.assessment_type_id)}
                      style={{ backgroundColor: "#10b981", minWidth: 80 }}
                      data-testid={`assessment-continue-btn-${item.assessment_type_id}`}
                    >
                      {expandedAssessment === item.assessment_type_id
                        ? <>Collapse <ChevronUp className="h-3 w-3" /></>
                        : <>Continue <ChevronDown className="h-3 w-3" /></>}
                    </Button>
                  )}
                </div>
              </div>

              {/* Inline Assessment Expansion — only for the current assessment */}
              {item.type === "assessment" && isCurrent && expandedAssessment === item.assessment_type_id && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4" data-testid={`assessment-inline-${item.assessment_type_id}`}>
                  {assessmentLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} /></div>
                  ) : assessmentItems.length === 0 ? (
                    <p className="text-gray-500 font-body text-sm text-center py-4">No items in this assessment.</p>
                  ) : (
                    <>
                      {assessmentItems.map((q, qIdx) => {
                        const fb = feedback[q.id];
                        const answered = !!fb;
                        const isSurvey = q.item_category === "survey";
                        return (
                          <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid={`assessment-question-${q.id}`}>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-body font-bold text-gray-400 mt-0.5">{qIdx + 1}.</span>
                              <div className="flex-1">
                                <div className="text-sm font-body text-gray-900 ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(q.stem) }} />
                                {!isSurvey && <span className="text-xs text-gray-400 font-body">{q.point_value} pt{q.point_value !== 1 ? "s" : ""}</span>}
                              </div>
                            </div>

                            {/* MC / TF */}
                            {(q.item_type === "mc") && (
                              <RadioGroup value={answered ? (fb.selected_option_ids || fb.correct_option_ids)?.[0] : (responses[q.id] || "")}
                                onValueChange={(v) => !answered && setItemResponse(q.id, v)} disabled={answered}>
                                {(q.answer_options || []).map((opt) => {
                                  const isCorrect = answered && !isSurvey && fb.correct_option_ids?.includes(opt.option_id);
                                  const isSelected = answered && (fb.selected_option_ids || []).includes(opt.option_id);
                                  let optClass = "";
                                  if (answered && !isSurvey) {
                                    if (isCorrect) optClass = "bg-emerald-50 border-emerald-200";
                                    else if (isSelected && !isCorrect) optClass = "bg-red-50 border-red-200";
                                  } else if (answered && isSurvey && isSelected) {
                                    optClass = "bg-blue-50 border-blue-200";
                                  }
                                  return (
                                    <label key={opt.option_id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${optClass || "border-gray-200 hover:bg-gray-50"}`}>
                                      <RadioGroupItem value={opt.option_id} id={opt.option_id} disabled={answered} />
                                      <span className="text-sm font-body text-gray-700">{opt.text}</span>
                                    </label>
                                  );
                                })}
                              </RadioGroup>
                            )}

                            {/* Select All */}
                            {q.item_type === "sata" && (
                              <div className="space-y-2">
                                {(q.answer_options || []).map((opt) => {
                                  const selected = (responses[q.id] || []).includes(opt.option_id);
                                  const isCorrect = answered && !isSurvey && fb.correct_option_ids?.includes(opt.option_id);
                                  const isSelected = answered && (fb.selected_option_ids || []).includes(opt.option_id);
                                  let optClass = "";
                                  if (answered && !isSurvey) {
                                    if (isCorrect) optClass = "bg-emerald-50 border-emerald-200";
                                    else if (selected && !isCorrect) optClass = "bg-red-50 border-red-200";
                                  } else if (answered && isSurvey && isSelected) {
                                    optClass = "bg-blue-50 border-blue-200";
                                  }
                                  return (
                                    <label key={opt.option_id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${optClass || "border-gray-200 hover:bg-gray-50"}`}>
                                      <Checkbox checked={answered ? (isSurvey ? (fb.selected_option_ids || []).includes(opt.option_id) : (fb.correct_option_ids || []).includes(opt.option_id)) : selected}
                                        onCheckedChange={() => {
                                          if (answered) return;
                                          const prev = responses[q.id] || [];
                                          setItemResponse(q.id, selected ? prev.filter((x) => x !== opt.option_id) : [...prev, opt.option_id]);
                                        }} disabled={answered} />
                                      <span className="text-sm font-body text-gray-700">{opt.text}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {/* Short Answer / Essay */}
                            {(q.item_type === "short_answer" || q.item_type === "essay") && (
                              <Textarea value={answered ? (fb.text_response || "") : (responses[q.id] || "")}
                                onChange={(e) => !answered && setItemResponse(q.id, e.target.value)}
                                disabled={answered} rows={q.item_type === "essay" ? 5 : 2}
                                className="border-gray-200 text-gray-900 font-body text-sm" placeholder="Type your answer..." />
                            )}

                            {/* Likert */}
                            {q.item_type === "likert" && (() => {
                              const scaleMin = q.likert_scale_min ?? 1;
                              const scaleMax = q.likert_scale_max ?? 5;
                              const values = Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => i + scaleMin);
                              const labels = q.likert_labels || {};
                              return (
                                <div>
                                  <div className="flex justify-between w-full">
                                    {values.map((val) => (
                                      <div key={val} className="flex flex-col items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                                        <button onClick={() => !answered && setItemResponse(q.id, val)} disabled={answered}
                                          className={`w-12 h-12 rounded-lg border text-sm font-body font-medium transition-colors ${
                                            (answered ? fb.likert_value === val : responses[q.id] === val)
                                              ? "border-transparent text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                                          }`}
                                          style={(answered ? fb.likert_value === val : responses[q.id] === val) ? { backgroundColor: "var(--primary-color, #4A90D9)" } : {}}>
                                          {val}
                                        </button>
                                        {labels[String(val)] && (
                                          <span className="text-[10px] text-gray-400 font-body mt-1.5 text-center leading-tight max-w-[72px]">{labels[String(val)]}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Submit / Feedback */}
                            {!answered && responses[q.id] && (
                              <Button size="sm" onClick={() => submitItemResponse(q)} disabled={submittingItem === q.id}
                                className="h-8 text-xs font-body rounded-lg hover:opacity-90"
                                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                                data-testid={`submit-item-${q.id}`}>
                                {submittingItem === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                                Submit
                              </Button>
                            )}
                            {answered && !isSurvey && fb.is_correct != null && (
                              <div className={`text-xs font-body font-medium ${fb.is_correct ? "text-emerald-600" : "text-red-500"}`}>
                                {fb.is_correct ? "Correct" : "Incorrect"} — {fb.earned_points}/{q.point_value} pts
                              </div>
                            )}
                            {answered && isSurvey && (
                              <div className="text-xs font-body font-medium text-blue-500">Response recorded</div>
                            )}
                          </div>
                        );
                      })}

                      {/* Complete Assessment */}
                      {!item.completed && assessmentItems.length > 0 && (
                        <div className="flex justify-end">
                          <Button onClick={() => completeAssessment(item.assessment_type_id)} disabled={completingAssessment}
                            className="h-9 px-5 text-sm font-body rounded-lg hover:opacity-90"
                            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                            data-testid={`complete-assessment-${item.assessment_type_id}`}>
                            {completingAssessment ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                            Complete Assessment
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {track.submitted && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="track-submitted-banner">
          <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <p className="font-body font-bold text-emerald-700">Track Submitted</p>
          {track.submission?.total_score != null && track.track_total_visible !== false && (
            <p className="text-sm text-emerald-600 font-body mt-1">Score: {track.submission.total_score}%</p>
          )}
          {track.track_total_visible === false && (
            <p className="text-sm text-gray-500 font-body mt-1" data-testid="track-score-pending">
              Score pending review
            </p>
          )}
        </div>
      )}

      {/* Submit Dialog */}
      <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
        <DialogContent className="max-w-md" data-testid="submit-dialog">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl">Submit Track</DialogTitle>
            <DialogDescription className="font-body text-gray-500">
              {subInfo?.submission_type === "custom" && subInfo.custom_submission_message
                ? subInfo.custom_submission_message
                : `You have completed ${subInfo?.items_completed || 0} of ${subInfo?.items_total || 0} items.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="flex justify-between text-sm font-body">
              <span className="text-gray-500">Items Completed</span>
              <span className="font-medium">{subInfo?.items_completed || 0} / {subInfo?.items_total || 0}</span>
            </div>
            <div className="flex justify-between text-sm font-body">
              <span className="text-gray-500">Score</span>
              <span className="font-medium">
                {track.track_total_visible === false ? "Pending review" : `${subInfo?.total_score || 0}%`}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowSubmit(false)} className="font-body">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}
              className="font-body rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="confirm-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Confirm Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
